import React from 'react'
import {Tabs} from 'antd'
import get from 'lodash/get'
import DescriptionList from 'src/components/elements/DescriptionList/DescriptionList'
import {
  ASSUMPTION_DOSAGE_SUB_FIELDS,
  SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA,
  SECONDARY_THERAPY_GROUPS,
  SecondaryTherapyDataSchemaElement
} from 'src/constants/secondaryTherapyFields'
import {ThreeColumnsGrid} from './TherapyGridStyled'
import {
  formatDate,
  formatDeviceOfAssumption,
  formatFrequencyDays,
  formatModeOfAssumption,
  formatTherapyEach,
  formatUnitOfMeasure
} from './therapyFormatters'

// Config-driven rendering of a SECONDARY (accessoria) therapy — legacy counterpart of
// src/pages-new/patient/tabs/therapy/SecondaryTherapyDetail.tsx. Field visibility/labels/order
// come from `dataSchema`; when the prop is omitted, SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA is
// used instead (see that constant for why).

type SecondaryTherapyCardTabsProps = {
  secondaryTherapy: Record<string, any>
  dataSchema?: SecondaryTherapyDataSchemaElement[]
}

// Same as before, but labels for the infusionRate/secondaryDosage/dilution sub-fields now come
// from the (unfiltered) dataSchema, and each sub-field is only shown if enabled in the config.
const renderCustomDosages = (dosages: Record<string, any>[] = [], dataSchema: SecondaryTherapyDataSchemaElement[]) => {
  const propertyIsInDataSchema = (key: string) => dataSchema.some(element => element.value === key)
  const propertyInDataSchemaName = (key: string) => dataSchema.find(element => element.value === key)?.name ?? key

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px'}}>
      <span style={{color: '#A6A6A6', fontSize: '12px'}}>{'Dosaggio personalizzato'}</span>
      <ThreeColumnsGrid>
        {dosages.map((dosage, index) => (
          <div key={index}>
            <b>{`Assunzione ${index + 1}:`}</b>
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0px 8px 8px'}}>
              <b>{`Dosaggio: ${formatUnitOfMeasure(dosage)}`}</b>
              {propertyIsInDataSchema('assumptionDosage.infusionRate') &&
                <span>{`${propertyInDataSchemaName('assumptionDosage.infusionRate')}: ${formatUnitOfMeasure(dosage.infusionRate)}`}</span>}
              {propertyIsInDataSchema('assumptionDosage.secondaryDosage') &&
                <span>{`${propertyInDataSchemaName('assumptionDosage.secondaryDosage')}: ${formatUnitOfMeasure(dosage.secondaryDosage)}`}</span>}
              {propertyIsInDataSchema('assumptionDosage.dilution') &&
                <span>{`${propertyInDataSchemaName('assumptionDosage.dilution')}: ${dosage.dilution ?? ''}`}</span>}
            </div>
          </div>
        ))}
      </ThreeColumnsGrid>
    </div>
  )
}

function renderField(element: SecondaryTherapyDataSchemaElement, therapy: Record<string, any>): {label: string, value: React.ReactNode} {
  const rawValue = get(therapy, element.value)

  switch (element.type) {
  case 'date':
    return {label: element.name, value: formatDate(rawValue)}
  case 'boolean':
    return {label: element.name, value: rawValue ? 'Si' : 'No'}
  case 'frequencyTimes':
    return {label: element.name, value: rawValue ? `${rawValue} ogni ${formatTherapyEach(get(therapy, 'frequency.each'))}` : ''}
  case 'frequencyDays':
    return {label: element.name, value: formatFrequencyDays(rawValue ?? [])}
  case 'valueAndUnitType':
  case 'assumptionDosage':
    return {label: element.name, value: formatUnitOfMeasure(rawValue)}
  case 'modeOfAssumption':
    return {label: element.name, value: formatModeOfAssumption(rawValue)}
  case 'device':
    return {label: element.name, value: formatDeviceOfAssumption(rawValue)}
  default:
    return {label: element.name, value: rawValue !== undefined && rawValue !== null ? String(rawValue) : ''}
  }
}

const SecondaryTherapyCardTabs: React.FC<SecondaryTherapyCardTabsProps> = ({secondaryTherapy, dataSchema}) => {
  const schema = dataSchema ?? SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA
  const {customDosages} = secondaryTherapy

  // When customDosages is present, the single-value assumptionDosage fields don't apply — the
  // "Dosaggi" tab instead shows one custom-dosages entry per assumption.
  const visibleSchema = customDosages
    ? schema.filter(element => !ASSUMPTION_DOSAGE_SUB_FIELDS.includes(element.value))
    : schema
  const dosagesGroupConfigured = schema.some(element => element.group === 'dosages')

  const items = SECONDARY_THERAPY_GROUPS.map(group => {
    const fields = visibleSchema.filter(element => element.group === group.key)
    const showCustomDosages = group.key === 'dosages' && Boolean(customDosages) && dosagesGroupConfigured
    if (fields.length === 0 && !showCustomDosages) return null
    return {
      label: group.label,
      key: group.key,
      children: (
        <>
          <DescriptionList items={fields.map(field => renderField(field, secondaryTherapy))} />
          {showCustomDosages && renderCustomDosages(customDosages, schema)}
        </>
      )
    }
  }).filter(Boolean) as {label: string, key: string, children: JSX.Element}[]

  if (items.length === 0) {
    return <span>{'Nessun campo configurato per la terapia accessoria.'}</span>
  }

  return (
    <Tabs
      items={items}
      size='small'
      type='card'
    />
  )
}

export default SecondaryTherapyCardTabs
